/**
 * MUI Table component that replaces the styled-components Table
 * 
 * This component provides a Material UI equivalent of the original Table component
 * with theme support and styling consistent with DesignsList table.
 */
import React from 'react';
import { 
  Table as MuiTableBase, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  styled,
  useTheme
} from '@mui/material';
import { TableProps } from 'types/components/molecules';

/**
 * MUI Table component
 * 
 * @param props - Component props
 * @returns MUI Table component
 */
const Table: React.FC<TableProps> = (props) => {
  const { headers = [], rows = [], className = '', ...otherProps } = props;
  const theme = useTheme();
  
  return (
    <TableContainer 
      component={Paper} 
      className={className}
      sx={{ 
        boxShadow: 'none',
        backgroundColor: theme.palette.background.paper,
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: theme.palette.background.paper,
        },
        '&::-webkit-scrollbar-thumb': {
          background: theme.palette.action.disabled,
          borderRadius: '4px',
          '&:hover': {
            background: theme.palette.action.selected,
          },
        },
      }}
    >
      <StyledTable {...otherProps}>
        <TableHead>
          <TableRow>
            {headers.map((header, index) => (
              <StyledTableHeaderCell key={`header-${index}`}>
                {header === 'qty' ? 'Quantity' : header.charAt(0).toUpperCase() + header.slice(1)}
              </StyledTableHeaderCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <StyledTableRow key={`row-${rowIndex}`}>
              {row.map((cell, cellIndex) => (
                <StyledTableCell key={`cell-${rowIndex}-${cellIndex}`}>
                  {cell}
                </StyledTableCell>
              ))}
            </StyledTableRow>
          ))}
        </TableBody>
      </StyledTable>
    </TableContainer>
  );
};

// Styled MUI Table components based on DesignsList table styling
const StyledTable = styled(MuiTableBase)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.background.paper,
  '& .MuiTableCell-root': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '& thead .MuiTableCell-root': {
    borderBottom: `2px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  '& tbody': {
    backgroundColor: theme.palette.background.default,
  },
}));

const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
  height: '56px',
  padding: '12px 16px',
  fontSize: '14px',
  fontFamily: 'Open Sans',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  verticalAlign: 'middle',
  borderBottom: `2px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  position: 'sticky',
  top: 0,
  zIndex: 10,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  height: '56px',
  padding: '12px 16px',
  fontFamily: 'Open Sans',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '150%', /* 21px */
  letterSpacing: '-0.28px',
  color: theme.palette.text.primary,
  verticalAlign: 'middle',
  borderBottom: `0.6px solid ${theme.palette.divider}`,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:first-of-type .MuiTableCell-root': {
    borderTop: 'none !important',
  },
  '& .MuiTableCell-root': {
    transition: 'background-color 0.2s ease',
  },
}));

export type { TableProps };
export default Table;
